import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3, TrendingUp, Clock, DollarSign, Download, Briefcase,
  Users, AlertTriangle, Percent, ChevronDown, ChevronRight, User, Receipt,
  FileText, Wallet, CheckCheck, TrendingDown, CalendarRange,
} from 'lucide-react';
import {
  Card, CardHeader, CardBody, Button, Select, Badge,
  EmptyState, KPI, MiniBarChart,
  Skeleton, SkeletonListRows,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { Can, AccessDenied } from '../contexts/PermissionsContext';
import { PlanGate } from '../contexts/PlanContext';
import {
  listMatters, listTimeEntries, listInvoices, listFirmUsers,
  listAllMatterPayments, listAllCaseCharges, listExpenses,
} from '../lib/db';
import { formatMoney, formatMoneyCompact, formatDuration, formatDate, classNames } from '../lib/formatters';
import { rangeStart, rangeWindow, priorWindow, pct } from '../lib/reportRange';

const RANGES = [
  { value: 'mtd',    label: 'Month to date' },
  { value: 'qtd',    label: 'Quarter to date' },
  { value: 'ytd',    label: 'Year to date' },
  { value: '30',     label: 'Last 30 days' },
  { value: '90',     label: 'Last 90 days' },
  { value: '365',    label: 'Last 12 months' },
  { value: 'all',    label: 'All time' },
  // v1.0.45: free-form picker. When selected, ReportsPage renders two
  // date inputs and uses them to build the window. Persisted to
  // localStorage along with the preset choice.
  { value: 'custom', label: 'Custom range…' },
];

// LocalStorage helpers. Per-firm keys so a user with access to
// multiple firms doesn't leak prefs across them. Defensive try/catch
// because Safari private mode and quota-exceeded both throw on write.
const prefsKey = (firmId) => `reports:prefs:${firmId || 'anon'}`;
function loadPrefs(firmId) {
  try {
    const raw = window.localStorage.getItem(prefsKey(firmId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function savePrefs(firmId, prefs) {
  try {
    window.localStorage.setItem(prefsKey(firmId), JSON.stringify(prefs));
  } catch {
    // Quota exceeded / private mode / disabled — ignore. The page
    // still works perfectly; the user just doesn't get the
    // remembered-range nicety.
  }
}

export default function ReportsPage() {
  // Reports show firm-wide revenue, per-employee breakdowns, and AR — all
  // of which is sensitive. Require both reports.view and finance.view_firm.
  return (
    <PlanGate feature="reports">
      <Can any={['reports.view']} fallback={<AccessDenied />}>
        <Can permission="finance.view_firm" fallback={<AccessDenied />}>
          <ReportsPageInner />
        </Can>
      </Can>
    </PlanGate>
  );
}

function ReportsPageInner() {
  const { firmId, refreshSignal } = useFirm();
  const [matters,     setMatters]     = useState([]);
  const [entries,     setEntries]     = useState([]);
  const [invoices,    setInvoices]    = useState([]);
  const [payments,    setPayments]    = useState([]);
  const [caseCharges, setCaseCharges] = useState([]);
  const [expenses,    setExpenses]    = useState([]);
  const [teamMap,     setTeamMap]     = useState({});   // userId → display name
  const [loading,     setLoading]     = useState(true);
  const [range,       setRange]       = useState('ytd');
  const [tab,         setTab]         = useState('overview');
  const [expandedEmp, setExpandedEmp] = useState(null);  // userId expanded in employee tab

  // v1.0.45: custom date range + prior-period comparison.
  // - customStart / customEnd are ISO date strings (YYYY-MM-DD) used
  //   only when range==='custom'. Empty strings → rangeWindow falls
  //   back to sensible defaults (1y ago → today).
  // - compareEnabled drives the delta arrows on every KPI in the
  //   Overview tab. Disabled automatically for range==='all' since
  //   the prior window there is degenerate.
  const [customStart,    setCustomStart]    = useState('');
  const [customEnd,      setCustomEnd]      = useState('');
  const [compareEnabled, setCompareEnabled] = useState(true);

  // Hydrate from localStorage once per firmId. We use a separate
  // useEffect from the data fetch so a slow data response doesn't
  // delay restoring the user's last-used preferences.
  useEffect(() => {
    if (!firmId) return;
    const p = loadPrefs(firmId);
    if (!p) return;
    if (p.range)        setRange(p.range);
    if (p.customStart)  setCustomStart(p.customStart);
    if (p.customEnd)    setCustomEnd(p.customEnd);
    if (typeof p.compareEnabled === 'boolean') setCompareEnabled(p.compareEnabled);
  }, [firmId]);

  // Persist any change back. Cheap to write — localStorage is sync
  // and writes are debounced by React's batching.
  useEffect(() => {
    if (!firmId) return;
    savePrefs(firmId, { range, customStart, customEnd, compareEnabled });
  }, [firmId, range, customStart, customEnd, compareEnabled]);

  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    (async () => {
      try {
        const [m, t, i, u, p, cc, ex] = await Promise.all([
          listMatters(firmId), listTimeEntries(firmId),
          listInvoices(firmId), listFirmUsers(firmId),
          listAllMatterPayments(firmId), listAllCaseCharges(firmId),
          listExpenses(firmId),
        ]);
        setMatters(m.data || []);
        setEntries(t.data || []);
        setInvoices(i.data || []);
        setPayments(p.data || []);
        setCaseCharges(cc.data || []);
        setExpenses(ex.data || []);
        const map = {};
        for (const user of (u.data || [])) {
          map[user.id] = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
        }
        setTeamMap(map);
      } finally {
        setLoading(false);
      }
    })();
  }, [firmId, refreshSignal]);

  // Current window: {start, end} based on the preset OR the custom
  // date inputs. End is "today" for presets (no end-date input there).
  const window = useMemo(
    () => rangeWindow(range, customStart, customEnd),
    [range, customStart, customEnd],
  );
  // Backward-compat alias — many downstream memos still reference `start`.
  const start = window.start;

  // Prior window for comparison. Same duration immediately preceding
  // `window`. Suppressed for 'all' since the prior would be degenerate.
  const showCompare = compareEnabled && range !== 'all';
  const prior = useMemo(
    () => showCompare ? priorWindow(window) : null,
    [window, showCompare],
  );

  // Date-field filter helper. Inclusive on both ends. Reused for the
  // prior-window filters below so the math is symmetrical.
  const inWindow = (d, w) => {
    const t = new Date(d).getTime();
    return t >= w.start.getTime() && t <= w.end.getTime();
  };

  const filteredEntries = useMemo(
    () => entries.filter((e) => e.entry_date && inWindow(e.entry_date, window)),
    [entries, window],
  );
  const filteredInvoices = useMemo(
    () => invoices.filter((i) => {
      const d = i.issue_date || i.created_at;
      return d && inWindow(d, window);
    }),
    [invoices, window],
  );
  const filteredPayments = useMemo(
    () => payments.filter((p) => p.payment_date && inWindow(p.payment_date, window)),
    [payments, window],
  );
  const filteredCharges = useMemo(
    () => caseCharges.filter((c) => c.charge_date && inWindow(c.charge_date, window)),
    [caseCharges, window],
  );
  const filteredExpenses = useMemo(
    () => expenses.filter((e) => e.expense_date && inWindow(e.expense_date, window)),
    [expenses, window],
  );

  // Prior-period filtered slices. Mirror the current-window ones so
  // we can recompute the SAME KPI shape on the prior data without
  // duplicating the field-name knowledge.
  const priorEntries  = useMemo(
    () => prior ? entries.filter((e) => e.entry_date && inWindow(e.entry_date, prior)) : [],
    [entries, prior],
  );
  const priorInvoices = useMemo(
    () => prior ? invoices.filter((i) => {
      const d = i.issue_date || i.created_at;
      return d && inWindow(d, prior);
    }) : [],
    [invoices, prior],
  );

  // ---------- Expenses by category ----------
  const expensesByCategory = useMemo(() => {
    const totals = new Map();
    for (const e of filteredExpenses) {
      const key = e.category || 'Uncategorized';
      totals.set(key, (totals.get(key) || 0) + Number(e.amount || 0));
    }
    return Array.from(totals.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredExpenses]);

  const totalExpenses    = useMemo(() => filteredExpenses.reduce((s, e) => s + Number(e.amount || 0), 0), [filteredExpenses]);
  const billableExpenses = useMemo(() => filteredExpenses.filter((e) => e.billable).reduce((s, e) => s + Number(e.amount || 0), 0), [filteredExpenses]);

  // ---------- Employee revenue (all users, attribution by responsible attorney) ----------
  const employeeRevenue = useMemo(() => {
    // Build a matter lookup: matterId → matter
    const matterById = {};
    for (const m of matters) matterById[m.id] = m;

    // Collect all user IDs that appear in entries, expenses, OR as responsible attorneys
    const userIds = new Set([
      ...filteredEntries.map((e) => e.user_id),
      ...filteredExpenses.map((e) => e.user_id),
      ...matters.map((m) => m.responsible_attorney_id),
    ].filter(Boolean));

    return Array.from(userIds).map((userId) => {
      // 1. Billable time entries for this user
      const myEntries = filteredEntries.filter((e) => e.user_id === userId && e.billable);
      const billableRev = myEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
      const billableMin = myEntries.reduce((s, e) => s + (e.duration_minutes || 0), 0);

      // 2. Matters where this user is responsible attorney
      const myMatterIds = new Set(
        matters.filter((m) => m.responsible_attorney_id === userId).map((m) => m.id),
      );

      // 3. Flat fees on those matters (range-filtered)
      const flatFees = filteredPayments
        .filter((p) => myMatterIds.has(p.matter_id))
        .reduce((s, p) => s + Number(p.amount || 0), 0);

      // 4. Extra charges on those matters (range-filtered)
      const extraCharges = filteredCharges
        .filter((c) => myMatterIds.has(c.matter_id))
        .reduce((s, c) => s + Number(c.amount || 0), 0);

      // 5. Expenses logged by this user
      const myExpenses = filteredExpenses.filter((e) => e.user_id === userId);
      const expensesTotal = myExpenses.reduce((s, e) => s + Number(e.amount || 0), 0);

      const total = billableRev + flatFees + extraCharges + expensesTotal;

      // 5. Per-matter breakdown for drill-down (only matters with any activity)
      const perMatter = Array.from(myMatterIds).map((mid) => {
        const m = matterById[mid];
        if (!m) return null;
        const mBillable = filteredEntries
          .filter((e) => e.user_id === userId && e.billable && e.matter_id === mid)
          .reduce((s, e) => s + Number(e.amount || 0), 0);
        const mFlat = filteredPayments
          .filter((p) => p.matter_id === mid)
          .reduce((s, p) => s + Number(p.amount || 0), 0);
        const mExtra = filteredCharges
          .filter((c) => c.matter_id === mid)
          .reduce((s, c) => s + Number(c.amount || 0), 0);
        const mExpenses = filteredExpenses
          .filter((e) => e.user_id === userId && e.matter_id === mid)
          .reduce((s, e) => s + Number(e.amount || 0), 0);
        const mTotal = mBillable + mFlat + mExtra + mExpenses;
        if (mTotal === 0) return null;
        return {
          id: mid,
          title: m.title,
          number: m.matter_number,
          practice_area: m.practice_area,
          status: m.status,
          billableRev: mBillable,
          flatFees: mFlat,
          extraCharges: mExtra,
          expenses: mExpenses,
          total: mTotal,
        };
      }).filter(Boolean).sort((a, b) => b.total - a.total);

      return { userId, billableRev, billableMin, flatFees, extraCharges, expenses: expensesTotal, total, perMatter };
    }).filter((r) => r.total > 0).sort((a, b) => b.total - a.total);
  }, [filteredEntries, filteredPayments, filteredCharges, filteredExpenses, matters]);

  // ---------- Top-line KPIs ----------
  // KPI computation extracted to a helper so the prior-period KPIs can
  // be computed against the same shape without duplication. Pure
  // function of two filtered arrays.
  const computeKpis = (entriesSlice, invoicesSlice) => {
    const billableMin = entriesSlice.filter((e) => e.billable).reduce((s, e) => s + (e.duration_minutes || 0), 0);
    const nonBillableMin = entriesSlice.filter((e) => !e.billable).reduce((s, e) => s + (e.duration_minutes || 0), 0);
    const totalMin = billableMin + nonBillableMin;
    const utilization = totalMin ? Math.round((billableMin / totalMin) * 100) : 0;
    const revenue = invoicesSlice.reduce((s, i) => s + Number(i.total || 0), 0);
    const collected = invoicesSlice.reduce((s, i) => s + Number(i.amount_paid || 0), 0);
    const outstanding = invoicesSlice
      .filter((i) => ['sent', 'viewed', 'partial', 'overdue'].includes(i.status))
      .reduce((s, i) => s + Number(i.balance || 0), 0);
    const realization = revenue ? Math.round((collected / revenue) * 100) : 0;
    return { billableMin, nonBillableMin, utilization, revenue, collected, outstanding, realization };
  };

  const kpis = useMemo(
    () => computeKpis(filteredEntries, filteredInvoices),
    [filteredEntries, filteredInvoices],
  );

  // Prior-period KPIs — same shape as `kpis`, computed against the
  // prior-window slices. Null when comparison is disabled so the
  // KPI strip can branch cleanly.
  const priorKpis = useMemo(
    () => showCompare ? computeKpis(priorEntries, priorInvoices) : null,
    [showCompare, priorEntries, priorInvoices],
  );

  // Pre-computed deltas for the KPI strip. `null` means "no delta to
  // show" (either compare is off, or the prior period had zero so
  // percent change is undefined).
  //
  // Sign convention: positive % = "current is bigger". For most KPIs
  // that's good (more revenue, more hours, higher utilization). For
  // Outstanding AR a DECREASE is good — we invert the sign there so
  // the KPI's emerald/red coloring lines up with "good direction."
  const deltas = useMemo(() => {
    if (!priorKpis) return {};
    const d = {
      billableMin:  pct(kpis.billableMin,  priorKpis.billableMin),
      utilization:  pct(kpis.utilization,  priorKpis.utilization),
      revenue:      pct(kpis.revenue,      priorKpis.revenue),
      collected:    pct(kpis.collected,    priorKpis.collected),
      // Inverted: down = good (less unpaid). UI shows emerald arrow.
      outstanding:  pct(kpis.outstanding,  priorKpis.outstanding),
      realization:  pct(kpis.realization,  priorKpis.realization),
    };
    if (d.outstanding != null) d.outstanding = -d.outstanding;
    return d;
  }, [kpis, priorKpis]);

  // ---------- Revenue by matter ----------
  const revenueByMatter = useMemo(() => {
    const totals = new Map();
    for (const e of filteredEntries) {
      if (!e.billable) continue;
      const key = e.matter_id || 'unassigned';
      const m = matters.find((x) => x.id === e.matter_id);
      const prev = totals.get(key) || {
        id: key, title: m?.title || 'Unassigned', number: m?.matter_number || '',
        hours: 0, amount: 0,
      };
      prev.hours += (e.duration_minutes || 0) / 60;
      prev.amount += Number(e.amount || 0);
      totals.set(key, prev);
    }
    return Array.from(totals.values())
      .sort((a, b) => b.amount - a.amount).slice(0, 10);
  }, [filteredEntries, matters]);

  // ---------- Revenue by practice area ----------
  const revenueByPractice = useMemo(() => {
    const totals = new Map();
    for (const e of filteredEntries) {
      if (!e.billable) continue;
      const m = matters.find((x) => x.id === e.matter_id);
      const key = m?.practice_area || 'Uncategorized';
      totals.set(key, (totals.get(key) || 0) + Number(e.amount || 0));
    }
    return Array.from(totals.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredEntries, matters]);

  // ---------- Revenue by attorney ----------
  const revenueByAttorney = useMemo(() => {
    const totals = new Map();
    for (const e of filteredEntries) {
      if (!e.billable) continue;
      const key = e.user_id || 'unassigned';
      const prev = totals.get(key) || { id: key, hours: 0, amount: 0 };
      prev.hours += (e.duration_minutes || 0) / 60;
      prev.amount += Number(e.amount || 0);
      totals.set(key, prev);
    }
    return Array.from(totals.values()).sort((a, b) => b.amount - a.amount);
  }, [filteredEntries]);

  // ---------- AR aging ----------
  const arAging = useMemo(() => {
    const buckets = { current: 0, '1-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    const now = Date.now();
    for (const inv of invoices) {
      if (!['sent', 'viewed', 'partial', 'overdue'].includes(inv.status)) continue;
      const bal = Number(inv.balance || 0);
      if (bal <= 0) continue;
      const due = inv.due_date ? new Date(inv.due_date).getTime() : null;
      const daysPastDue = due ? Math.floor((now - due) / 86400000) : 0;
      if (daysPastDue <= 0) buckets.current += bal;
      else if (daysPastDue <= 30) buckets['1-30'] += bal;
      else if (daysPastDue <= 60) buckets['31-60'] += bal;
      else if (daysPastDue <= 90) buckets['61-90'] += bal;
      else buckets['90+'] += bal;
    }
    return buckets;
  }, [invoices]);

  // ---------- 12-week revenue trend ----------
  const revenueTrend = useMemo(() => {
    const weeks = 12;
    const buckets = Array.from({ length: weeks }, (_, i) => {
      const start = new Date();
      start.setDate(start.getDate() - (weeks - 1 - i) * 7);
      start.setHours(0,0,0,0);
      const end = new Date(start); end.setDate(end.getDate() + 7);
      const sum = entries
        .filter((e) => e.billable && new Date(e.entry_date) >= start && new Date(e.entry_date) < end)
        .reduce((s, e) => s + Number(e.amount || 0), 0);
      return { label: `${start.getMonth() + 1}/${start.getDate()}`, value: sum };
    });
    return buckets;
  }, [entries]);

  // ---------- Matter profitability ----------
  const matterProfitability = useMemo(() => {
    return matters.map((m) => {
      const mEntries = entries.filter((e) => e.matter_id === m.id);
      const billableAmount = mEntries.filter((e) => e.billable).reduce((s, e) => s + Number(e.amount || 0), 0);
      const totalMin = mEntries.reduce((s, e) => s + (e.duration_minutes || 0), 0);
      const billableMin = mEntries.filter((e) => e.billable).reduce((s, e) => s + (e.duration_minutes || 0), 0);
      const budget = Number(m.budget_amount || 0);
      const utilization = totalMin ? Math.round((billableMin / totalMin) * 100) : 0;
      const overBudget = budget > 0 && billableAmount > budget;
      return { ...m, billableAmount, totalMin, billableMin, budget, utilization, overBudget };
    }).filter((r) => r.totalMin > 0)
      .sort((a, b) => b.billableAmount - a.billableAmount)
      .slice(0, 10);
  }, [matters, entries]);


  // ---------- WIP Aging (unbilled time, grouped by age) ----------
  const wipByMatter = useMemo(() => {
    // Collect ALL time entries that are billable and not yet invoiced
    // We approximate "not invoiced" by checking if the matter has unpaid/draft invoices
    // For simplicity: any billable entry on a matter where invoice total < billable amount
    const matterBillable = new Map(); // matterId → { matter, entries: [] }
    for (const e of entries) {
      if (!e.billable) continue;
      const m = matters.find((x) => x.id === e.matter_id);
      if (!m || m.status === 'archived') continue;
      if (!matterBillable.has(e.matter_id)) {
        matterBillable.set(e.matter_id, { matter: m, entries: [] });
      }
      matterBillable.get(e.matter_id).entries.push(e);
    }
    // For each matter, subtract invoiced amounts to find WIP
    const result = [];
    for (const [matterId, { matter, entries: mEntries }] of matterBillable) {
      const totalBillable = mEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
      const invoiced = invoices
        .filter((i) => i.matter_id === matterId && i.status !== 'void')
        .reduce((s, i) => s + Number(i.total || 0), 0);
      const wip = Math.max(0, totalBillable - invoiced);
      if (wip < 1) continue;
      // Find oldest unbilled entry
      const sortedEntries = [...mEntries].sort((a, b) => new Date(a.entry_date) - new Date(b.entry_date));
      const oldestDate = sortedEntries[0]?.entry_date;
      const daysOld = oldestDate ? Math.floor((Date.now() - new Date(oldestDate)) / 86400000) : 0;
      const hours = mEntries.reduce((s, e) => s + (e.duration_minutes || 0) / 60, 0);
      result.push({ id: matterId, title: matter.title, number: matter.matter_number,
        practice_area: matter.practice_area, wip, hours, daysOld, oldestDate });
    }
    return result.sort((a, b) => b.wip - a.wip);
  }, [entries, matters, invoices]);

  const wipBuckets = useMemo(() => {
    const b = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    for (const m of wipByMatter) {
      if (m.daysOld <= 30) b['0-30'] += m.wip;
      else if (m.daysOld <= 60) b['31-60'] += m.wip;
      else if (m.daysOld <= 90) b['61-90'] += m.wip;
      else b['90+'] += m.wip;
    }
    return b;
  }, [wipByMatter]);

  const totalWip = useMemo(() => wipByMatter.reduce((s, m) => s + m.wip, 0), [wipByMatter]);

  // ---------- Collections ----------
  const collectionsData = useMemo(() => {
    return invoices
      .filter((i) => i.status !== 'void' && Number(i.total || 0) > 0)
      .map((i) => {
        const total = Number(i.total || 0);
        const paid = Number(i.amount_paid || 0);
        const balance = Number(i.balance || 0);
        const daysOld = i.due_date
          ? Math.floor((Date.now() - new Date(i.due_date)) / 86400000)
          : null;
        const matter = matters.find((m) => m.id === i.matter_id);
        return { ...i, total, paid, balance, daysOld, matterTitle: matter?.title || '—', matterNumber: matter?.matter_number || '' };
      })
      .sort((a, b) => b.daysOld - a.daysOld);
  }, [invoices, matters]);

  const collectionRate = useMemo(() => {
    const invoiced = collectionsData.reduce((s, i) => s + i.total, 0);
    const collected = collectionsData.reduce((s, i) => s + i.paid, 0);
    return invoiced > 0 ? Math.round((collected / invoiced) * 100) : 0;
  }, [collectionsData]);

  const exportCsv = () => {
    // Header row shows the active window so the CSV is self-describing.
    // Custom ranges render their YYYY-MM-DD bounds; presets show their
    // label.
    const rangeLabel = range === 'custom'
      ? `${window.start.toISOString().slice(0,10)} to ${window.end.toISOString().slice(0,10)}`
      : RANGES.find((r) => r.value === range)?.label;

    // KPI rows: when comparison is on, append a "vs prior" column with
    // the prior value AND the delta percentage. Lets accountants verify
    // the page numbers against the underlying data without losing the
    // delta context.
    const kpiRow = (label, curr, priorValue, delta) =>
      priorKpis
        ? [label, curr, priorValue, delta == null ? '—' : `${delta >= 0 ? '+' : ''}${delta}%`]
        : [label, curr];
    const kpiHeader = priorKpis
      ? ['Metric', 'Current', 'Prior period', 'Δ']
      : ['Metric', 'Value'];

    const rows = [
      ['Report', 'Range', rangeLabel],
      ...(priorKpis ? [['Prior period', prior.start.toISOString().slice(0,10), prior.end.toISOString().slice(0,10)]] : []),
      [], ['KPIs'],
      kpiHeader,
      kpiRow('Billable hours',
        formatDuration(kpis.billableMin),
        priorKpis ? formatDuration(priorKpis.billableMin) : null,
        deltas.billableMin),
      kpiRow('Non-billable hours',
        formatDuration(kpis.nonBillableMin),
        priorKpis ? formatDuration(priorKpis.nonBillableMin) : null,
        null),
      kpiRow('Utilization %',
        `${kpis.utilization}%`,
        priorKpis ? `${priorKpis.utilization}%` : null,
        deltas.utilization),
      kpiRow('Revenue invoiced',
        formatMoney(kpis.revenue),
        priorKpis ? formatMoney(priorKpis.revenue) : null,
        deltas.revenue),
      kpiRow('Collected',
        formatMoney(kpis.collected),
        priorKpis ? formatMoney(priorKpis.collected) : null,
        deltas.collected),
      kpiRow('Outstanding',
        formatMoney(kpis.outstanding),
        priorKpis ? formatMoney(priorKpis.outstanding) : null,
        // Re-invert for the CSV so accountants see the raw direction
        // (positive means the AR balance grew). The UI inverts for
        // good/bad coloring; the CSV stays literal.
        deltas.outstanding == null ? null : -deltas.outstanding),
      kpiRow('Realization %',
        `${kpis.realization}%`,
        priorKpis ? `${priorKpis.realization}%` : null,
        deltas.realization),
      [], ['Revenue by Matter'],
      ['Matter #', 'Title', 'Hours', 'Amount'],
      ...revenueByMatter.map((r) => [r.number, r.title, r.hours.toFixed(2), r.amount.toFixed(2)]),
      [], ['Revenue by Practice Area'],
      ['Area', 'Amount'],
      ...revenueByPractice.map((r) => [r.label, r.value.toFixed(2)]),
      [], ['AR Aging'],
      ['Bucket', 'Amount'],
      ...Object.entries(arAging).map(([k, v]) => [k, v.toFixed(2)]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `report-${range}-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <BarChart3 size={22} /> Reports & Analytics
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Loading the latest numbers…</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <KPI key={i} label="" value="" loading icon={BarChart3} tone="indigo" />
          ))}
        </div>
        <Card><CardBody><Skeleton className="h-32 w-full" /></CardBody></Card>
        <Card><CardBody><SkeletonListRows rows={5} /></CardBody></Card>
      </div>
    );
  }

  // True-empty state: firm hasn't logged time, sent invoices, or recorded
  // anything yet. Show a single onboarding-focused card instead of a sea
  // of empty charts.
  const isFullyEmpty =
    entries.length === 0 && invoices.length === 0 && payments.length === 0 && matters.length === 0;
  if (isFullyEmpty) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <BarChart3 size={22} /> Reports & Analytics
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Revenue, utilization, AR aging, and matter profitability
          </p>
        </div>
        <Card>
          <CardBody>
            <EmptyState
              icon={BarChart3}
              title="Nothing to report yet"
              description="Reports populate automatically as you open matters, log time, send invoices and record payments. Come back here once your firm has a few weeks of activity — or load sample data from the dashboard checklist to see what reports look like."
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <BarChart3 size={22} /> Reports & Analytics
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Revenue, utilization, AR aging, and matter profitability
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={range} onChange={(e) => setRange(e.target.value)}
            options={RANGES.map((r) => ({ value: r.value, label: r.label }))} />
          {/* Custom range inputs — only rendered when 'Custom range…'
              is selected. Native HTML date inputs (browser-styled) so
              we don't pull in a calendar library for a marginal UX win.
              Empty values fall back to sensible defaults inside
              rangeWindow() (1 year ago → today). */}
          {range === 'custom' && (
            <>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                aria-label="Custom range start"
                className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-md px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-slate-400 text-sm">→</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                aria-label="Custom range end"
                className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-md px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </>
          )}
          {/* Prior-period compare toggle. Disabled for 'all' since the
              prior window would be degenerate (start = epoch) and the
              deltas meaningless. */}
          <label
            className={classNames(
              'flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border cursor-pointer select-none transition-colors',
              range === 'all'
                ? 'border-slate-200 dark:border-slate-800 text-slate-400 cursor-not-allowed'
                : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50',
            )}
            title={range === 'all' ? 'Comparison is unavailable for the All time range' : 'Show delta arrows comparing each KPI against the equivalent prior period'}
          >
            <input
              type="checkbox"
              checked={range !== 'all' && compareEnabled}
              disabled={range === 'all'}
              onChange={(e) => setCompareEnabled(e.target.checked)}
              className="rounded border-slate-400"
            />
            Compare to prior period
          </label>
          {['overview','wip','collections'].includes(tab) && (
            <Button variant="secondary" icon={Download} onClick={exportCsv}>Export CSV</Button>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700">
        {[
          { id: 'overview',     label: 'Overview',          icon: BarChart3 },
          { id: 'wip',          label: 'WIP Aging',         icon: CalendarRange },
          { id: 'collections',  label: 'Collections',       icon: CheckCheck },
          { id: 'employee',     label: 'Employee Revenue',  icon: Users },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={classNames(
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === id
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300',
            )}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <>
      {/* KPIs — six cards. Money values use compact notation (GH₵12K /
          GH₵3.4M) so the cards stay readable at lg:grid-cols-6 width;
          the full amount is shown in the tooltip. */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <KPI label="Billable hours"   value={formatDuration(kpis.billableMin)}      delta={deltas.billableMin} icon={Clock}        tone="indigo" />
        <KPI label="Utilization"      value={`${kpis.utilization}%`}                delta={deltas.utilization} icon={Percent}      tone="green" />
        <KPI label="Revenue invoiced" value={formatMoneyCompact(kpis.revenue)}      delta={deltas.revenue}     title={formatMoney(kpis.revenue)}    icon={DollarSign}   tone="indigo" />
        <KPI label="Collected"        value={formatMoneyCompact(kpis.collected)}    delta={deltas.collected}   title={formatMoney(kpis.collected)}  icon={Wallet}       tone="green" />
        {/* Outstanding AR: delta is pre-inverted (down = good) so the
            KPI's emerald arrow lines up with the "good direction" of
            a decreasing receivables balance. */}
        <KPI label="Outstanding AR"   value={formatMoneyCompact(kpis.outstanding)}  delta={deltas.outstanding} title={formatMoney(kpis.outstanding)} icon={TrendingDown} tone="amber" />
        <KPI label="Realization"      value={`${kpis.realization}%`}                delta={deltas.realization} icon={TrendingUp}   tone="amber" />
      </div>
      {/* Prior-period label: clarifies what the deltas are compared
          against. Only shown when comparison is active. */}
      {showCompare && prior && (
        <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2">
          Comparing against {prior.start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} – {prior.end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Revenue trend (last 12 weeks)"
            subtitle="Billable time × rate, per week"
            action={<TrendingUp size={16} className="text-slate-400" />}
          />
          <CardBody>
            <MiniBarChart data={revenueTrend} height={100} />
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>{revenueTrend[0]?.label}</span>
              <span>{revenueTrend[revenueTrend.length - 1]?.label}</span>
            </div>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="AR aging" subtitle="Outstanding invoices by age" />
          <CardBody className="space-y-2">
            {Object.entries(arAging).map(([k, v]) => {
              const tone = k === 'current' ? 'green' : k === '1-30' ? 'blue' : k === '31-60' ? 'amber' : 'red';
              return (
                <div key={k} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Badge tone={tone}>{k}</Badge>
                    <span className="text-slate-500">days</span>
                  </div>
                  <span className="font-mono font-semibold">{formatMoney(v)}</span>
                </div>
              );
            })}
            <div className="h-px bg-slate-200 dark:bg-slate-700 my-2" />
            <div className="flex items-center justify-between text-sm font-semibold">
              <span>Total AR</span>
              <span className="font-mono">{formatMoney(Object.values(arAging).reduce((s, v) => s + v, 0))}</span>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue by practice area */}
        <Card>
          <CardHeader title="Revenue by practice area" action={<Briefcase size={16} className="text-slate-400" />} />
          <CardBody className="p-0">
            {!revenueByPractice.length ? (
              <EmptyState title="No billable time yet" />
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {revenueByPractice.map((r) => {
                  const max = revenueByPractice[0].value || 1;
                  const pct = (r.value / max) * 100;
                  return (
                    <li key={r.label} className="px-6 py-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{r.label}</span>
                        <span className="font-mono">{formatMoney(r.value)}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                        <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* Top matters by revenue */}
        <Card>
          <CardHeader title="Top 10 matters by revenue" action={<DollarSign size={16} className="text-slate-400" />} />
          <CardBody className="p-0">
            {!revenueByMatter.length ? (
              <EmptyState title="No billable time yet" />
            ) : (
              <>
                <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {revenueByMatter.map((r) => (
                    <li key={r.id} className="px-4 py-3 flex justify-between items-center gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{r.title}</p>
                        <p className="text-xs text-slate-500">{r.number}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-mono font-semibold text-sm">{formatMoney(r.amount)}</p>
                        <p className="text-xs text-slate-500">{r.hours.toFixed(1)}h</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="hidden sm:block">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                    <tr>
                      <th className="text-left px-6 py-2 font-medium">Matter</th>
                      <th className="text-right px-6 py-2 font-medium">Hours</th>
                      <th className="text-right px-6 py-2 font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {revenueByMatter.map((r) => (
                      <tr key={r.id}>
                        <td className="px-6 py-2.5">
                          <p className="font-medium truncate">{r.title}</p>
                          <p className="text-xs text-slate-500">{r.number}</p>
                        </td>
                        <td className="px-6 py-2.5 text-right text-slate-500">{r.hours.toFixed(1)}h</td>
                        <td className="px-6 py-2.5 text-right font-mono font-semibold">{formatMoney(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Expenses breakdown */}
      <Card>
        <CardHeader
          title="Expenses"
          subtitle="Firm expenses by category in this period"
          action={<Receipt size={16} className="text-slate-400" />}
        />
        <CardBody className="p-0">
          {!expensesByCategory.length ? (
            <EmptyState title="No expenses recorded in this range" />
          ) : (
            <>
              {/* Three side-by-side cells inside a single card — the
                  columns get narrow on tablets and short laptops, so
                  use compact notation ("GH₵12K") for the big number and
                  put the full value in a `title` tooltip on hover. */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-slate-100 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-800">
                <div className="bg-white dark:bg-slate-900 px-6 py-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Total expenses</p>
                  <p className="text-xl font-bold font-mono mt-0.5 truncate" title={formatMoney(totalExpenses)}>
                    {formatMoneyCompact(totalExpenses)}
                  </p>
                </div>
                <div className="bg-white dark:bg-slate-900 px-6 py-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Billable expenses</p>
                  <p className="text-xl font-bold font-mono mt-0.5 text-indigo-600 truncate" title={formatMoney(billableExpenses)}>
                    {formatMoneyCompact(billableExpenses)}
                  </p>
                </div>
                <div className="bg-white dark:bg-slate-900 px-6 py-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Non-billable</p>
                  <p className="text-xl font-bold font-mono mt-0.5 text-slate-400 truncate" title={formatMoney(totalExpenses - billableExpenses)}>
                    {formatMoneyCompact(totalExpenses - billableExpenses)}
                  </p>
                </div>
              </div>
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {expensesByCategory.map((r) => {
                  const max = expensesByCategory[0].value || 1;
                  const pct = (r.value / max) * 100;
                  return (
                    <li key={r.label} className="px-6 py-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{r.label}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-400">{Math.round((r.value / totalExpenses) * 100)}%</span>
                          <span className="font-mono font-semibold">{formatMoney(r.value)}</span>
                        </div>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                        <div className="h-full bg-rose-400" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </CardBody>
      </Card>

      {/* Matter profitability */}
      <Card>
        <CardHeader
          title="Matter profitability"
          subtitle="Hours worked, billable amount, and budget variance"
          action={<AlertTriangle size={16} className="text-slate-400" />}
        />
        <CardBody className="p-0">
          {!matterProfitability.length ? (
            <EmptyState
              title="Log some time first"
              description="This report ranks matters by hours billed vs. fees collected. As soon as you log billable time and record payments, you'll see which matters are profitable and which aren't."
            />
          ) : (
            <>
              <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                {matterProfitability.map((m) => (
                  <li key={m.id} className="px-4 py-3 flex justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{m.title}</p>
                      <p className="text-xs text-slate-500">{m.matter_number} · {m.practice_area || '—'}</p>
                      {m.overBudget ? <Badge tone="red">Over budget</Badge>
                        : m.budget ? <Badge tone="green">Within budget</Badge>
                        : <Badge tone="slate">No budget</Badge>}
                    </div>
                    <div className="text-right flex-shrink-0 text-xs text-slate-500 space-y-0.5">
                      <p>{(m.totalMin / 60).toFixed(1)}h · {m.utilization}%</p>
                      <p className="font-mono font-semibold text-slate-800 dark:text-slate-200">{formatMoney(m.billableAmount)}</p>
                      {m.budget && <p className="font-mono">Budget: {formatMoney(m.budget)}</p>}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium">Matter</th>
                    <th className="text-right px-6 py-2 font-medium">Hours</th>
                    <th className="text-right px-6 py-2 font-medium">Utilization</th>
                    <th className="text-right px-6 py-2 font-medium">Billable</th>
                    <th className="text-right px-6 py-2 font-medium">Budget</th>
                    <th className="text-left px-6 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {matterProfitability.map((m) => (
                    <tr key={m.id}>
                      <td className="px-6 py-2.5">
                        <p className="font-medium">{m.title}</p>
                        <p className="text-xs text-slate-500">{m.matter_number} · {m.practice_area || '—'}</p>
                      </td>
                      <td className="px-6 py-2.5 text-right">{(m.totalMin / 60).toFixed(1)}h</td>
                      <td className="px-6 py-2.5 text-right">{m.utilization}%</td>
                      <td className="px-6 py-2.5 text-right font-mono">{formatMoney(m.billableAmount)}</td>
                      <td className="px-6 py-2.5 text-right font-mono text-slate-500">{m.budget ? formatMoney(m.budget) : '—'}</td>
                      <td className="px-6 py-2.5">
                        {m.overBudget ? <Badge tone="red">Over budget</Badge>
                          : m.budget ? <Badge tone="green">Within budget</Badge>
                          : <Badge tone="slate">No budget</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Attorney productivity */}
      <Card>
        <CardHeader title="Timekeeper productivity" action={<Users size={16} className="text-slate-400" />} />
        <CardBody className="p-0">
          {!revenueByAttorney.length ? (
            <EmptyState title="No time entries in this range" />
          ) : (
            <>
              <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                {revenueByAttorney.map((r) => (
                  <li key={r.id} className="px-4 py-3 flex justify-between gap-2">
                    <p className="font-medium text-sm">{teamMap[r.id] || r.id.slice(0, 8)}</p>
                    <div className="text-right text-xs text-slate-500 space-y-0.5">
                      <p>{r.hours.toFixed(1)}h billable</p>
                      <p className="font-mono font-semibold text-slate-800 dark:text-slate-200">{formatMoney(r.amount)}</p>
                      <p>{r.hours > 0 ? formatMoney(r.amount / r.hours) : '—'}/h avg</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="hidden sm:block">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium">User</th>
                    <th className="text-right px-6 py-2 font-medium">Billable hours</th>
                    <th className="text-right px-6 py-2 font-medium">Fees generated</th>
                    <th className="text-right px-6 py-2 font-medium">Avg rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {revenueByAttorney.map((r) => (
                    <tr key={r.id}>
                      <td className="px-6 py-2.5 font-medium">{teamMap[r.id] || r.id.slice(0, 8)}</td>
                      <td className="px-6 py-2.5 text-right">{r.hours.toFixed(1)}h</td>
                      <td className="px-6 py-2.5 text-right font-mono font-semibold">{formatMoney(r.amount)}</td>
                      <td className="px-6 py-2.5 text-right text-slate-500">{r.hours > 0 ? formatMoney(r.amount / r.hours) : '—'}/h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>
      </>}


      {tab === 'wip' && <>
        {/* WIP summary KPIs. text-2xl in a 2-up grid at 375px viewport
            is tight — even formatMoney(0) ("GH₵0.00") plus surrounding
            padding overflows when the actual value is 6+ digits. Use
            compact for the big number, full in the tooltip. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total WIP</p>
            <p className="text-2xl font-bold font-mono mt-1 truncate" title={formatMoney(totalWip)}>
              {formatMoneyCompact(totalWip)}
            </p>
            <p className="text-xs text-slate-400 mt-1">Unbilled billable time</p>
          </div>
          {Object.entries(wipBuckets).map(([bucket, amount]) => {
            const tone = bucket === '0-30' ? 'green' : bucket === '31-60' ? 'blue' : bucket === '61-90' ? 'amber' : 'red';
            const colors = { green: 'text-emerald-600', blue: 'text-blue-600', amber: 'text-amber-600', red: 'text-red-600' };
            return (
              <div key={bucket} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
                <p className="text-xs text-slate-500 uppercase tracking-wide">{bucket} days</p>
                <p className={`text-2xl font-bold font-mono mt-1 truncate ${colors[tone]}`} title={formatMoney(amount)}>
                  {formatMoneyCompact(amount)}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {totalWip > 0 ? `${Math.round((amount / totalWip) * 100)}% of WIP` : '0% of WIP'}
                </p>
              </div>
            );
          })}
        </div>
        {/* WIP aging bar */}
        {totalWip > 0 && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">WIP age distribution</p>
            <div className="flex h-4 rounded-full overflow-hidden gap-px">
              {Object.entries(wipBuckets).map(([bucket, amount]) => {
                const pct = totalWip > 0 ? (amount / totalWip) * 100 : 0;
                const bg = { '0-30': 'bg-emerald-400', '31-60': 'bg-blue-400', '61-90': 'bg-amber-400', '90+': 'bg-red-400' };
                return pct > 0 ? <div key={bucket} style={{ width: `${pct}%` }} className={`${bg[bucket]} transition-all`} title={`${bucket}d: ${formatMoney(amount)}`} /> : null;
              })}
            </div>
            <div className="flex gap-4 mt-2">
              {[['0-30','bg-emerald-400'], ['31-60','bg-blue-400'], ['61-90','bg-amber-400'], ['90+','bg-red-400']].map(([b,c]) => (
                <div key={b} className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span className={`w-2.5 h-2.5 rounded-sm ${c}`} />{b}d
                </div>
              ))}
            </div>
          </div>
        )}
        {/* WIP by matter */}
        <Card>
          <CardHeader title="WIP by matter" subtitle="Unbilled billable time per active case file" action={<FileText size={16} className="text-slate-400" />} />
          <CardBody className="p-0">
            {!wipByMatter.length ? (
              <EmptyState
                title="No unbilled work in progress"
                description="Either your team has invoiced everything (good!) or nobody has logged time since the last invoice run. Either way, this number staying near zero is healthy."
              />
            ) : (
              <>
                {/* Mobile: stacked card list — five columns of WIP data
                    don't fit on a phone, and the horizontal-scroll
                    fallback hides the most-important column (WIP value)
                    behind a scroll the user often doesn't notice. */}
                <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {wipByMatter.map((m) => {
                    const ageTone = m.daysOld <= 30 ? 'green' : m.daysOld <= 60 ? 'blue' : m.daysOld <= 90 ? 'amber' : 'red';
                    return (
                      <li key={m.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm truncate">{m.title}</p>
                            <p className="text-xs text-slate-400 truncate">
                              {m.number}{m.practice_area ? ` · ${m.practice_area}` : ''}
                            </p>
                          </div>
                          <p className="font-mono font-bold text-sm text-indigo-700 dark:text-indigo-400 flex-shrink-0"
                             title={formatMoney(m.wip)}>
                            {formatMoneyCompact(m.wip)}
                          </p>
                        </div>
                        <div className="mt-1.5 flex items-center justify-between gap-2">
                          <p className="text-xs text-slate-500">
                            {m.hours.toFixed(1)}h &middot; oldest {formatDate(m.oldestDate)}
                          </p>
                          <Badge tone={ageTone}>{m.daysOld}d old</Badge>
                        </div>
                      </li>
                    );
                  })}
                  <li className="px-4 py-3 bg-slate-50 dark:bg-slate-800/40 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total WIP</span>
                    <span className="font-mono font-bold text-indigo-700 dark:text-indigo-400" title={formatMoney(totalWip)}>
                      {formatMoneyCompact(totalWip)}
                    </span>
                  </li>
                </ul>

                {/* Desktop / tablet: original table */}
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                    <tr>
                      <th className="text-left px-6 py-2 font-medium">Matter</th>
                      <th className="text-right px-4 py-2 font-medium">Hours</th>
                      <th className="text-right px-4 py-2 font-medium">WIP value</th>
                      <th className="text-right px-4 py-2 font-medium">Oldest entry</th>
                      <th className="text-left px-6 py-2 font-medium">Age</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {wipByMatter.map((m) => {
                      const ageTone = m.daysOld <= 30 ? 'green' : m.daysOld <= 60 ? 'blue' : m.daysOld <= 90 ? 'amber' : 'red';
                      return (
                        <tr key={m.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <td className="px-6 py-3">
                            <p className="font-medium">{m.title}</p>
                            <p className="text-xs text-slate-400">{m.number} {m.practice_area ? `· ${m.practice_area}` : ''}</p>
                          </td>
                          <td className="px-4 py-3 text-right text-slate-500">{m.hours.toFixed(1)}h</td>
                          <td className="px-4 py-3 text-right font-mono font-semibold text-indigo-700 dark:text-indigo-400">{formatMoney(m.wip)}</td>
                          <td className="px-4 py-3 text-right text-slate-500">{formatDate(m.oldestDate)}</td>
                          <td className="px-6 py-3"><Badge tone={ageTone}>{m.daysOld}d old</Badge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 font-semibold">
                      <td colSpan={2} className="px-6 py-3 text-sm">Total</td>
                      <td className="px-4 py-3 text-right font-mono text-indigo-700 dark:text-indigo-400">{formatMoney(totalWip)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </>}

      {tab === 'collections' && <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Collection rate</p>
            <p className="text-2xl font-bold mt-1">{collectionRate}%</p>
            <p className="text-xs text-slate-400 mt-1">Collected / invoiced</p>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total invoiced</p>
            {(() => {
              const v = collectionsData.reduce((s,i)=>s+i.total,0);
              return <p className="text-2xl font-bold font-mono mt-1 truncate" title={formatMoney(v)}>{formatMoneyCompact(v)}</p>;
            })()}
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Collected</p>
            {(() => {
              const v = collectionsData.reduce((s,i)=>s+i.paid,0);
              return <p className="text-2xl font-bold font-mono mt-1 truncate text-emerald-600" title={formatMoney(v)}>{formatMoneyCompact(v)}</p>;
            })()}
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Outstanding</p>
            {(() => {
              const v = collectionsData.reduce((s,i)=>s+i.balance,0);
              return <p className="text-2xl font-bold font-mono mt-1 truncate text-amber-600" title={formatMoney(v)}>{formatMoneyCompact(v)}</p>;
            })()}
          </div>
        </div>
        <Card>
          <CardHeader title="Invoice collections" subtitle="All invoices with payment status and aging" action={<CheckCheck size={16} className="text-slate-400" />} />
          <CardBody className="p-0">
            {!collectionsData.length ? (
              <EmptyState title="No invoices yet" />
            ) : (
              <>
                {/* Mobile: stacked card list — 7 columns of invoice data
                    don't fit on a phone and the horizontal-scroll
                    fallback obscures the balance owed (the most useful
                    column for AR follow-up). */}
                <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {collectionsData.map((inv) => {
                    const pastDue = inv.daysOld > 0 ? inv.daysOld : null;
                    const statusTone = inv.status === 'paid' ? 'green' : inv.status === 'overdue' ? 'red' : inv.status === 'draft' ? 'slate' : 'blue';
                    return (
                      <li key={inv.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm">{inv.invoice_number || inv.id.slice(0,8)}</p>
                            <p className="text-xs text-slate-400 truncate">{inv.matterTitle}</p>
                            <p className="text-xs text-slate-400">{formatDate(inv.issue_date)}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <p className="font-mono font-bold text-sm" title={`Balance ${formatMoney(inv.balance)}`}>
                              {formatMoneyCompact(inv.balance)}
                            </p>
                            <Badge tone={statusTone}>{inv.status}</Badge>
                          </div>
                        </div>
                        <div className="mt-1.5 flex items-center justify-between text-xs">
                          <span className="text-slate-500">
                            <span className="text-slate-400">Invoiced</span>{' '}
                            <span className="font-mono" title={formatMoney(inv.total)}>{formatMoneyCompact(inv.total)}</span>
                            {' · '}
                            <span className="text-slate-400">Paid</span>{' '}
                            <span className="font-mono text-emerald-600" title={formatMoney(inv.paid)}>{formatMoneyCompact(inv.paid)}</span>
                          </span>
                          {pastDue && (
                            <Badge tone={pastDue > 60 ? 'red' : pastDue > 30 ? 'amber' : 'blue'}>{pastDue}d past due</Badge>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {/* Desktop / tablet: original table */}
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                    <tr>
                      <th className="text-left px-6 py-2 font-medium">Invoice</th>
                      <th className="text-left px-4 py-2 font-medium">Matter</th>
                      <th className="text-right px-4 py-2 font-medium">Invoiced</th>
                      <th className="text-right px-4 py-2 font-medium">Collected</th>
                      <th className="text-right px-4 py-2 font-medium">Balance</th>
                      <th className="text-left px-4 py-2 font-medium">Status</th>
                      <th className="text-right px-6 py-2 font-medium">Days past due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {collectionsData.map((inv) => {
                      const pastDue = inv.daysOld > 0 ? inv.daysOld : null;
                      const statusTone = inv.status === 'paid' ? 'green' : inv.status === 'overdue' ? 'red' : inv.status === 'draft' ? 'slate' : 'blue';
                      return (
                        <tr key={inv.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                          <td className="px-6 py-3">
                            <p className="font-medium">{inv.invoice_number || inv.id.slice(0,8)}</p>
                            <p className="text-xs text-slate-400">{formatDate(inv.issue_date)}</p>
                          </td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-300 max-w-[180px] truncate">{inv.matterTitle}</td>
                          <td className="px-4 py-3 text-right font-mono">{formatMoney(inv.total)}</td>
                          <td className="px-4 py-3 text-right font-mono text-emerald-600">{formatMoney(inv.paid)}</td>
                          <td className="px-4 py-3 text-right font-mono font-semibold">{formatMoney(inv.balance)}</td>
                          <td className="px-4 py-3"><Badge tone={statusTone}>{inv.status}</Badge></td>
                          <td className="px-6 py-3 text-right">
                            {pastDue ? <Badge tone={pastDue > 60 ? 'red' : pastDue > 30 ? 'amber' : 'blue'}>{pastDue}d</Badge> : <span className="text-slate-400">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </>}

      {tab === 'employee' && (
        <Card>
          <CardHeader
            title="Employee revenue"
            subtitle="Billable time + flat fees + extra charges attributed per responsible attorney"
            action={<User size={16} className="text-slate-400" />}
          />
          <CardBody className="p-0">
            {!employeeRevenue.length ? (
              <EmptyState title="No revenue data in this range" />
            ) : (
              <>
              {/* Mobile: stacked employee cards with tap-to-expand for
                  the per-matter breakdown. The 8-column desktop table
                  needs min-w-[700px] just to read — on a phone the user
                  was scrolling horizontally to see anything past the
                  employee name, which made the report unusable. */}
              <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                {employeeRevenue.map((emp) => {
                  const isExpanded = expandedEmp === emp.userId;
                  return (
                    <li key={emp.userId} className="bg-white dark:bg-slate-900">
                      <button
                        type="button"
                        onClick={() => setExpandedEmp(isExpanded ? null : emp.userId)}
                        className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/40 transition"
                      >
                        <div className="w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <User size={13} className="text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {teamMap[emp.userId] || emp.userId.slice(0, 8)}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {(emp.billableMin / 60).toFixed(1)}h billable
                            {emp.expenses > 0 && ` · ${formatMoneyCompact(emp.expenses)} expenses`}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-mono font-bold text-sm text-indigo-700 dark:text-indigo-400" title={formatMoney(emp.total)}>
                            {formatMoneyCompact(emp.total)}
                          </p>
                          {isExpanded
                            ? <ChevronDown size={14} className="ml-auto mt-1 text-slate-400" />
                            : <ChevronRight size={14} className="ml-auto mt-1 text-slate-400" />
                          }
                        </div>
                      </button>
                      {isExpanded && emp.perMatter.length > 0 && (
                        <ul className="bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-700">
                          {emp.perMatter.map((m) => (
                            <li key={m.id} className="px-4 py-2.5 pl-12 border-b border-slate-100 dark:border-slate-700/50 last:border-b-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{m.title}</p>
                                  <p className="text-[11px] text-slate-400">{m.number}{m.practice_area ? ` · ${m.practice_area}` : ''}</p>
                                </div>
                                <p className="font-mono font-semibold text-xs text-indigo-700 dark:text-indigo-400 flex-shrink-0" title={formatMoney(m.total)}>
                                  {formatMoneyCompact(m.total)}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
                <li className="px-4 py-3 bg-slate-50 dark:bg-slate-800/40 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Grand total</span>
                  <span className="font-mono font-bold text-indigo-700 dark:text-indigo-400"
                        title={formatMoney(employeeRevenue.reduce((s, e) => s + e.total, 0))}>
                    {formatMoneyCompact(employeeRevenue.reduce((s, e) => s + e.total, 0))}
                  </span>
                </li>
              </ul>

              <div className="hidden sm:block overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium w-8"></th>
                    <th className="text-left px-6 py-2 font-medium">Employee</th>
                    <th className="text-right px-4 py-2 font-medium">Billable hours</th>
                    <th className="text-right px-4 py-2 font-medium">Billable rev.</th>
                    <th className="text-right px-4 py-2 font-medium">Flat fees</th>
                    <th className="text-right px-4 py-2 font-medium">Extra charges</th>
                    <th className="text-right px-4 py-2 font-medium">Expenses</th>
                    <th className="text-right px-6 py-2 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {employeeRevenue.map((emp) => {
                    const isExpanded = expandedEmp === emp.userId;
                    return (
                      <React.Fragment key={emp.userId}>
                        <tr
                          className={classNames(
                            'cursor-pointer transition-colors',
                            isExpanded
                              ? 'bg-indigo-50 dark:bg-indigo-900/20'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800/40',
                          )}
                          onClick={() => setExpandedEmp(isExpanded ? null : emp.userId)}
                        >
                          <td className="pl-6 py-3 text-slate-400">
                            {isExpanded
                              ? <ChevronDown size={14} />
                              : <ChevronRight size={14} />}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                                <User size={13} className="text-indigo-600 dark:text-indigo-400" />
                              </div>
                              <span className="font-medium">
                                {teamMap[emp.userId] || emp.userId.slice(0, 8)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-slate-500">
                            {(emp.billableMin / 60).toFixed(1)}h
                          </td>
                          <td className="px-4 py-3 text-right font-mono">
                            {formatMoney(emp.billableRev)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono">
                            {formatMoney(emp.flatFees)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono">
                            {formatMoney(emp.extraCharges)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-rose-600 dark:text-rose-400">
                            {formatMoney(emp.expenses)}
                          </td>
                          <td className="px-6 py-3 text-right font-mono font-bold text-indigo-700 dark:text-indigo-400">
                            {formatMoney(emp.total)}
                          </td>
                        </tr>

                        {isExpanded && emp.perMatter.length > 0 && (
                          <tr>
                            <td colSpan={8} className="p-0">
                              <div className="bg-slate-50 dark:bg-slate-800/30 border-t border-b border-slate-100 dark:border-slate-700">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-slate-400 uppercase tracking-wide">
                                      <th className="text-left pl-16 pr-4 py-2 font-medium">Case file</th>
                                      <th className="text-left px-4 py-2 font-medium">Practice area</th>
                                      <th className="text-left px-4 py-2 font-medium">Status</th>
                                      <th className="text-right px-4 py-2 font-medium">Billable rev.</th>
                                      <th className="text-right px-4 py-2 font-medium">Flat fees</th>
                                      <th className="text-right px-4 py-2 font-medium">Extra charges</th>
                                      <th className="text-right px-4 py-2 font-medium">Expenses</th>
                                      <th className="text-right px-6 py-2 font-medium">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                                    {emp.perMatter.map((m) => (
                                      <tr key={m.id} className="hover:bg-white dark:hover:bg-slate-800/50 transition-colors">
                                        <td className="pl-16 pr-4 py-2.5">
                                          <p className="font-medium text-slate-700 dark:text-slate-300 truncate max-w-xs">{m.title}</p>
                                          <p className="text-slate-400">{m.number}</p>
                                        </td>
                                        <td className="px-4 py-2.5 text-slate-500">{m.practice_area || '—'}</td>
                                        <td className="px-4 py-2.5">
                                          <Badge tone={
                                            m.status === 'active' ? 'green'
                                            : m.status === 'closed' ? 'slate'
                                            : 'amber'
                                          }>
                                            {m.status || '—'}
                                          </Badge>
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono">{formatMoney(m.billableRev)}</td>
                                        <td className="px-4 py-2.5 text-right font-mono">{formatMoney(m.flatFees)}</td>
                                        <td className="px-4 py-2.5 text-right font-mono">{formatMoney(m.extraCharges)}</td>
                                        <td className="px-4 py-2.5 text-right font-mono text-rose-600 dark:text-rose-400">{formatMoney(m.expenses)}</td>
                                        <td className="px-6 py-2.5 text-right font-mono font-semibold text-indigo-700 dark:text-indigo-400">{formatMoney(m.total)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr className="border-t border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-semibold">
                                      <td colSpan={3} className="pl-16 pr-4 py-2.5">Total</td>
                                      <td className="px-4 py-2.5 text-right font-mono">{formatMoney(emp.billableRev)}</td>
                                      <td className="px-4 py-2.5 text-right font-mono">{formatMoney(emp.flatFees)}</td>
                                      <td className="px-4 py-2.5 text-right font-mono">{formatMoney(emp.extraCharges)}</td>
                                      <td className="px-4 py-2.5 text-right font-mono text-rose-600 dark:text-rose-400">{formatMoney(emp.expenses)}</td>
                                      <td className="px-6 py-2.5 text-right font-mono font-bold text-indigo-700 dark:text-indigo-400">{formatMoney(emp.total)}</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 font-semibold">
                    <td colSpan={2} className="px-6 py-3 text-sm">Grand total</td>
                    <td className="px-4 py-3 text-right text-sm text-slate-500">
                      {(employeeRevenue.reduce((s, e) => s + e.billableMin, 0) / 60).toFixed(1)}h
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm">
                      {formatMoney(employeeRevenue.reduce((s, e) => s + e.billableRev, 0))}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm">
                      {formatMoney(employeeRevenue.reduce((s, e) => s + e.flatFees, 0))}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm">
                      {formatMoney(employeeRevenue.reduce((s, e) => s + e.extraCharges, 0))}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-rose-600 dark:text-rose-400">
                      {formatMoney(employeeRevenue.reduce((s, e) => s + e.expenses, 0))}
                    </td>
                    <td className="px-6 py-3 text-right font-mono text-sm text-indigo-700 dark:text-indigo-400">
                      {formatMoney(employeeRevenue.reduce((s, e) => s + e.total, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
              </div>
              </>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
